import { Box } from '@mui/material';
import { colorSet, typography } from '@theme';
import { useCallback, useRef, useId, useState, useEffect } from 'react';

interface IAutosizeTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  defaultRow?: number;
  placeholder?: string;
  boxstyle?: React.CSSProperties;
  style?: React.CSSProperties;
}

// AutosizeTextarea + 일부 텍스트 primary color로 highlight 기능 (3D 상세 모달의 기본 프롬프트 value)
export const ImageGenerateTextarea = (props: IAutosizeTextareaProps) => {
  const { defaultRow = 1, placeholder, boxstyle, style, onChange, id, name, value, ...textareaProps } = props;

  const lineHeight = 20;
  const padding = 24;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const defaultId = useId();
  const [textareaHeight, setTextareaHeight] = useState(lineHeight * defaultRow);

  const calculateHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return lineHeight * defaultRow;

    const minHeight = lineHeight * defaultRow;

    // 내용이 없으면 최소 높이
    if (textarea.value.length === 0) {
      return minHeight;
    }

    // 높이를 minHeight로 일시적으로 설정하여 scrollHeight 정확히 측정
    const prevHeight = textarea.style.height;
    textarea.style.height = `${minHeight}px`;
    const scrollHeight = textarea.scrollHeight;
    textarea.style.height = prevHeight;

    // scrollHeight가 minHeight보다 크면 실제 내용이 넘친 것
    if (scrollHeight > minHeight) {
      return scrollHeight;
    }

    return minHeight;
  }, [defaultRow, lineHeight]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newHeight = calculateHeight();
      setTextareaHeight(newHeight);
      if (onChange) {
        onChange(e);
      }
    },
    [onChange, calculateHeight]
  );

  // defaultRow 변경 감지하여 높이 재계산
  useEffect(() => {
    const newHeight = calculateHeight();
    setTextareaHeight(newHeight);
  }, [defaultRow, calculateHeight]);

  // {text} 형식을 하이라이팅하여 렌더링
  const renderHighlightedText = () => {
    const text = (value as string) || '';
    if (!text) return null;

    // {로 시작하고 }로 끝나는 패턴 찾기
    const regex = /(\{[^}]*\})/g;
    const parts = text.split(regex);

    return parts.map((part, index) => {
      // {text} 형식인 경우
      if (part.startsWith('{') && part.endsWith('}')) {
        return (
          <span key={index} style={{ color: colorSet.primaryMain }}>
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: textareaHeight + padding,
        padding: '12px 16px',
        borderRadius: '5px',
        border: `1px solid ${colorSet.gray300}`,
        backgroundColor: colorSet.white,
        position: 'relative',
        '&:hover': {
          border: `1px solid ${colorSet.primaryMain}`
        },
        '&:focus-within': {
          border: `1px solid ${colorSet.gray600}`,
          boxShadow: 'none',
          outline: 'none'
        },
        ...boxstyle
      }}
    >
      {/* 하이라이팅된 텍스트 표시 레이어 */}
      <div
        style={{
          ...typography.body1,
          lineHeight: `${lineHeight}px`,
          width: 'calc(100% - 32px)', // 부모의 좌우 패딩 제외
          height: textareaHeight,
          padding: 0,
          margin: 0,
          border: 'none',
          overflow: 'hidden',
          boxSizing: 'border-box',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          pointerEvents: 'none',
          position: 'absolute',
          top: '12px',
          left: '16px',
          color: colorSet.gray600
        }}
      >
        {renderHighlightedText()}
      </div>

      {/* 실제 입력을 받는 textarea (투명) */}
      <textarea
        ref={textareaRef}
        {...textareaProps}
        id={id || defaultId}
        name={name}
        value={value}
        placeholder={placeholder}
        style={{
          ...typography.body1,
          lineHeight: `${lineHeight}px`,
          width: '100%',
          height: textareaHeight,
          padding: 0,
          margin: 0,
          outline: 'none',
          resize: 'none',
          border: 'none',
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundColor: 'transparent',
          color: 'transparent',
          caretColor: colorSet.gray600,
          position: 'relative',
          zIndex: 1,
          ...style
        }}
        onChange={handleChange}
      />
    </Box>
  );
};
